import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';
import { HttpStatus } from '@montugroup/apis-styleguide';
import { escriptConformanceId, useSession } from '@montugroup/circuit-shared';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import { sessionStore } from '@montugroup/circuit-shared/store/index';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const logger = new Logger('useAxiosSetup');

logger.info('init');
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.common.Authorization = `Bearer ${sessionStore.getState().session?.token}`;
axios.defaults.headers.common['Conformance-Id'] = escriptConformanceId;
axios.defaults.baseURL = settings.url;

export const useAxiosSetup = () => {
  const [searchParams] = useSearchParams();
  const { status, session } = useSession();
  const isLoggedIn = status === 'authenticated';

  const token = searchParams.get('token');

  useEffect(() => {
    const cleanUpFunctions: Array<() => void> = [];

    // NOTE: Logging out on request if the auth token has expired is a short term solution to prevent a logged in UI flash if we are about to log the user out anyway
    if (!token && session) {
      const tokenExpiry = session.tokenExpiry && DateTime.fromSeconds(session.tokenExpiry);

      const expiredAuthRequestInterceptor = axios.interceptors.request.use(
        (config) => {
          const now = DateTime.now();

          if (tokenExpiry && now > tokenExpiry) {
            logger.info('logging out due to token expiry', {
              hasToken: !!token,
              hasLocalStorageUser: !!session,
            });
            AuthService.logout({ reason: 'expired' });
            return Promise.reject(new Error('Token has expired'));
          }

          return config;
        },
        (error) => Promise.reject(error),
      );

      cleanUpFunctions.push(() => axios.interceptors.request.eject(expiredAuthRequestInterceptor));
    }

    if (!token && session) {
      const expiredAuthReponseInterceptor = axios.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
          const status = error?.response?.status;
          if (status === HttpStatus.UNAUTHORIZED && isLoggedIn) {
            logger.info('logging out due to UNAUTHORIZED error', {
              hasToken: !!token,
              hasLocalStorageUser: !!session,
            });
            AuthService.logout({ reason: 'expired' });
            return;
          }

          delete error.config?.headers?.Authorization;
          delete error.response?.config?.headers?.Authorization;

          return Promise.reject(error);
        },
      );

      cleanUpFunctions.push(() => axios.interceptors.response.eject(expiredAuthReponseInterceptor));
    }

    return () => {
      for (const cleanUpFunction of cleanUpFunctions) {
        cleanUpFunction();
      }
    };
  }, [isLoggedIn, session, token]);
};
